<template>
  <div v-click-outside="closePicker" class="reserve-date-nav" @keydown.esc="closePicker">
    <div class="reserve-date-nav__toolbar">
      <v-btn
        ref="monthButton"
        text
        class="reserve-date-nav__month"
        :aria-expanded="String(expanded)"
        aria-controls="reserve-date-picker"
        @click="togglePicker"
      >
        <span aria-live="polite">{{ expanded ? browsingMonthLabel : monthLabel }}</span>
        <v-icon v-if="$vuetify.breakpoint.smAndDown" right size="20">{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </v-btn>
      <v-btn outlined class="reserve-date-nav__today" @click="goToday">今日</v-btn>
      <div class="reserve-date-nav__desktop-arrows">
        <v-btn icon :aria-label="`上一${periodLabel}`" @click="$emit('prev')"><v-icon>mdi-chevron-left</v-icon></v-btn>
        <v-btn icon :aria-label="`下一${periodLabel}`" @click="$emit('next')"><v-icon>mdi-chevron-right</v-icon></v-btn>
      </div>
      <slot />
    </div>

    <div
      v-if="expanded"
      id="reserve-date-picker"
      class="reserve-date-nav__picker"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
    >
      <div class="reserve-date-nav__picker-heading">
        <v-btn icon aria-label="上一個月" @click="browseMonth(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
        <v-btn icon aria-label="下一個月" @click="browseMonth(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
      </div>
      <div class="reserve-date-nav__grid">
        <span v-for="day in weekdays" :key="day" class="reserve-date-nav__weekday">{{ day }}</span>
        <button
          v-for="day in monthDays"
          :key="day.date"
          type="button"
          class="reserve-date-nav__date"
          :class="{
            'reserve-date-nav__date--outside': !day.inMonth,
            'reserve-date-nav__date--today': day.date === today,
            'reserve-date-nav__date--selected': day.date === selectedDate
          }"
          :aria-label="day.label"
          :aria-pressed="String(day.date === selectedDate)"
          :aria-current="day.date === today ? 'date' : null"
          @click="selectDate(day.date)"
        ><span>{{ day.number }}</span></button>
      </div>
    </div>

    <div v-if="type !== 'day'" class="reserve-date-nav__date-bar">
      <v-btn :icon="type === 'week'" :text="type === 'month'" :aria-label="`上一${periodLabel}`" @click="$emit('prev')">
        <v-icon>mdi-chevron-left</v-icon><span v-if="type === 'month'">上個月</span>
      </v-btn>
      <span v-if="type === 'week'" aria-live="polite">{{ weekRangeLabel }}</span>
      <v-btn :icon="type === 'week'" :text="type === 'month'" :aria-label="`下一${periodLabel}`" @click="$emit('next')">
        <span v-if="type === 'month'">下個月</span><v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'ReserveDateNavigator',
  props: {
    value: { type: String, default: '' },
    type: { type: String, default: 'day' }
  },
  data() {
    return {
      expanded: false,
      browsingMonth: '',
      touchStart: null,
      weekdays: ['日', '一', '二', '三', '四', '五', '六']
    };
  },
  computed: {
    today() { return moment().format('YYYY-MM-DD'); },
    selectedDate() { return this.value ? moment(this.value).format('YYYY-MM-DD') : this.today; },
    monthLabel() { return moment(this.selectedDate).format('YYYY年M月'); },
    browsingMonthLabel() { return moment(this.browsingMonth, 'YYYY-MM').format('YYYY年M月'); },
    weekRangeLabel() {
      const date = moment(this.selectedDate);
      const start = date.clone().subtract(date.day(), 'days');
      return `${start.format('M/D')}－${start.clone().add(6, 'days').format('M/D')}`;
    },
    periodLabel() { return { day: '天', week: '週', month: '個月' }[this.type] || '天'; },
    monthDays() {
      const month = moment(this.browsingMonth, 'YYYY-MM');
      const first = month.clone().startOf('month');
      const start = first.clone().subtract(first.day(), 'days');
      const count = Math.ceil((first.day() + month.daysInMonth()) / 7) * 7;
      return Array.from({ length: count }, (_, index) => {
        const date = start.clone().add(index, 'days');
        return {
          date: date.format('YYYY-MM-DD'),
          label: `${date.format('YYYY年M月D日')} 星期${this.weekdays[date.day()]}`,
          number: date.date(),
          inMonth: date.month() === month.month()
        };
      });
    }
  },
  watch: {
    selectedDate() {
      this.browsingMonth = this.selectedDate.slice(0, 7);
    }
  },
  methods: {
    togglePicker() {
      if (!this.expanded) this.browsingMonth = this.selectedDate.slice(0, 7);
      this.expanded = !this.expanded;
    },
    closePicker() { this.expanded = false; },
    browseMonth(amount) {
      this.browsingMonth = moment(this.browsingMonth, 'YYYY-MM').add(amount, 'months').format('YYYY-MM');
    },
    selectDate(date) {
      this.$emit('input', date);
      this.closePicker();
      this.$nextTick(() => this.$refs.monthButton.$el.focus());
    },
    goToday() {
      this.closePicker();
      this.$emit('today');
    },
    onTouchStart(event) {
      const touch = event.changedTouches[0];
      this.touchStart = { x: touch.clientX, y: touch.clientY };
    },
    onTouchEnd(event) {
      if (!this.touchStart) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - this.touchStart.x;
      const dy = touch.clientY - this.touchStart.y;
      if (Math.abs(dx) > 60 && Math.abs(dy) < 40) this.browseMonth(dx < 0 ? 1 : -1);
      this.touchStart = null;
    }
  }
};
</script>

<style scoped lang="scss">
.reserve-date-nav {
  position: relative;
  color: #3c4043;
  background: white;
}
.reserve-date-nav__toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 64px;
}
.reserve-date-nav .v-btn { letter-spacing: normal; }
.reserve-date-nav__month.v-btn {
  height: 44px;
  padding: 0 8px;
  font-size: 22px;
  font-weight: 500;
}
.reserve-date-nav__month:focus:not(:focus-visible)::before { opacity: 0; }
.reserve-date-nav__today.v-btn { min-width: 72px; height: 40px; border-color: #616161; color: #3c4043; }
.reserve-date-nav__desktop-arrows { display: flex; margin-right: auto; }
.reserve-date-nav__desktop-arrows .v-btn,
.reserve-date-nav__picker .v-btn,
.reserve-date-nav__date-bar .v-btn--icon { width: 44px; height: 44px; }
.reserve-date-nav__picker {
  position: absolute;
  top: 60px;
  left: 0;
  z-index: 5;
  width: 336px;
  padding: 8px 12px 12px;
  background: white;
  border: 1px solid #dadce0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(60, 64, 67, .18);
}
.reserve-date-nav__picker-heading { display: flex; justify-content: flex-end; align-items: center; min-height: 48px; }
.reserve-date-nav__grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); }
.reserve-date-nav__weekday { text-align: center; line-height: 28px; font-size: 12px; color: #5f6368; }
.reserve-date-nav__date {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 44px;
  font-size: 14px;
  border-radius: 50%;
  touch-action: manipulation;
}
.reserve-date-nav__date span { display: grid; place-items: center; width: 36px; height: 36px; border-radius: 50%; }
.reserve-date-nav__date:hover span { background: #f1f3f4; }
.reserve-date-nav__date--outside { color: #757575; }
.reserve-date-nav__date--today span { box-shadow: inset 0 0 0 1px var(--v-primary-base); }
.reserve-date-nav__date--selected span,
.reserve-date-nav__date--selected:hover span { background: var(--v-primary-base); color: #103b38; }
.reserve-date-nav__date:focus-visible { outline: 2px solid #3c4043; outline-offset: -2px; }
.reserve-date-nav__date-bar { display: none; }
@media (max-width: 599px) {
  .reserve-date-nav__toolbar { min-height: 56px; gap: 8px; }
  .reserve-date-nav__month.v-btn { margin-right: auto; font-size: 20px; padding: 0 4px; }
  .reserve-date-nav__today.v-btn { min-width: 72px; height: 44px; padding: 0 8px; }
  .reserve-date-nav__desktop-arrows { display: none; }
  .reserve-date-nav__picker { position: relative; top: auto; width: 100%; padding: 0 0 8px; margin-bottom: 12px; border: 0; border-radius: 0; box-shadow: none; }
  .reserve-date-nav__picker::after {
    content: '';
    position: absolute;
    left: -12px;
    right: -12px;
    bottom: 0;
    height: 1px;
    box-shadow: 0 3px 6px 1px rgba(60, 64, 67, .16);
    pointer-events: none;
  }
  .reserve-date-nav__date-bar { display: flex; align-items: center; justify-content: space-between; border-top: 1px solid #f1f3f4; min-height: 48px; }
  .reserve-date-nav__date-bar span { font-size: 14px; font-weight: 500; }
}
</style>
